import { CompleteEvent, Model, SurveyModel } from "survey-core";

import { DateTime } from "@bps/date-time";
import { confirm } from "@bps/fluent-ui";

import { getDocumentDownloadData, postForm } from "../api/form";
import {
  asResponseMap,
  asVariableMap,
  toLocalDateTime
} from "../utils/setupModelHelpers";
import { renderers } from "../Widgets/replaceWidget";
import { FormDisplaySettings, FormStatusEnum } from "./useFormApi.types";

class FormConfig {
  hideFields: string[] | undefined;
  titleFields?: Record<string, string> | undefined;
}

const applyVariablesToModel = (
  model: Model,
  variableMap: Record<string, any>
) => {
  Object.keys(variableMap).forEach(key => {
    model.setVariable(key, variableMap[key]);
  });
};

export const setupModel: (options: {
  modelJson: string;
  expiry?: DateTime;
  setFormStatus?: (data: any) => void;
  formConfig?: FormConfig;
  formId?: string;
  pin?: string;
  qrcode: boolean;
  preview: boolean;
  formDisplaySettings?: FormDisplaySettings;
  isReadOnly?: boolean;
  documentDownloadCallBackUrl?: string;
  variables?: any;
  responseData?: any;
}) => { form: Model; titleFields?: Record<string, string> } = ({
  modelJson,
  expiry,
  setFormStatus,
  formId,
  pin,
  qrcode,
  formConfig,
  preview,
  isReadOnly,
  documentDownloadCallBackUrl,
  formDisplaySettings,
  variables,
  responseData
}) => {
  const responseValues = asResponseMap(responseData);

  const model = new Model(modelJson);

  const parsedModelJson = JSON.parse(modelJson);
  const templateVariables = asVariableMap(parsedModelJson.variables);
  const incomingVariables = asVariableMap(variables);

  const mergedVariables = {
    ...templateVariables,
    ...incomingVariables,
    expiryDateLocal: toLocalDateTime(expiry)
  };

  model.showCompleteButton = !isReadOnly;
  model.mode = isReadOnly ? "display" : "edit";
  applyVariablesToModel(model, mergedVariables);
  model.setVariable("_isReadOnly", isReadOnly ?? false);
  if (formId) {
    model.setVariable("_getDocumentDownloadData", () =>
      getDocumentDownloadData(formId, pin)
    );
  }
  model.data = responseValues;

  const hideFields = formConfig && formConfig.hideFields;
  const titleFields =
    formConfig && formConfig.titleFields ? formConfig.titleFields : undefined;

  model.getAllQuestions().forEach(x => {
    const renderer = renderers.get(x.getType());
    x.renderAs = renderer ?? x.renderAs;
    if (hideFields && hideFields.find(h => h === x.name)) {
      x.visible = false;
    }
  });

  const onComplete: (
    sender: SurveyModel,
    options: CompleteEvent
  ) => void = async (survey, options) => {
    try {
      options.showDataSavingClear();
      const result =
        !preview && formId && (await postForm(formId, survey.data, pin));
      if (!preview && setFormStatus) {
        if (!result) {
          formId && options.showDataSavingError();
        } else {
          !formId && showData(survey);

          const submittedIsReadOnly = !!result.readOnly;
          if (submittedIsReadOnly && result.formData) {
            setupModel({
              modelJson: result.formData,
              expiry: result.expiryDate
                ? DateTime.fromISO(result.expiryDate)
                : expiry,
              setFormStatus,
              formConfig: result.formConfiguration,
              formId,
              pin,
              qrcode,
              preview,
              formDisplaySettings:
                result.formDisplaySettings ?? formDisplaySettings,
              isReadOnly: true,
              documentDownloadCallBackUrl:
                result.documentDownloadCallBackUrl ??
                documentDownloadCallBackUrl,
              variables: result.variables ?? variables,
              responseData: result.responseData ?? survey.data
            });
            return;
          }

          setFormStatus({
            status: FormStatusEnum.Completed,
            formDisplaySettings,
            isReadOnly,
            documentDownloadCallBackUrl,
            variables
          });
        }
      } else {
        showData(survey);
      }
    } catch {
      options.showDataSavingError();
    }
  };

  let status = FormStatusEnum.Available;
  if (isReadOnly) {
    status = FormStatusEnum.IsReadOnly;
  } else if (qrcode) {
    status = FormStatusEnum.QrCode;
  }

  model && model.onComplete.add((model, event) => onComplete(model, event));
  model &&
    setFormStatus &&
    setFormStatus({
      status,
      isReadOnly,
      documentDownloadCallBackUrl,
      form: model,
      expiry,
      titleFields,
      preview,
      formDisplaySettings,
      variables
    });
  return { form: model, titleFields };
};

const createResponsePreview = (survey: Model): string => {
  const responses = [];
  for (const key in survey.data) {
    const question = survey
      .getAllQuestions()
      .find(question => question.name === key);
    responses.push({
      sortOrder: question?.visibleIndex,
      text: `${question?.title}: ${survey.data[key]}`
    });
  }
  const response = responses
    .sort((a, b) => (a.sortOrder ?? 1000) - (b.sortOrder ?? 999))
    .map(item => item.text)
    .join("\n\n");
  return response.substring(0, response.length);
};

const showData = (survey: Model) => {
  confirm({
    dialogContentProps: {
      title: "Form data",
      subText: createResponsePreview(survey),
      isMultiline: true,
      styles: { subText: { whiteSpace: "pre-line" } }
    },
    cancelButtonProps: {
      styles: { root: { display: "none" } }
    }
  });
};
